// src/pages/SearchDialog.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { IClientListItem, searchClients } from '../api/clients';
import { apiErrorMessage } from '../api/errors';
import { ClientCardDialog } from '../components/Chat/ClientCardDialog';
import { channelLabel } from '../components/Chat/clientChannels';
import type { Conversation } from '../types';
import { conversationSearchTitle, matchConversations } from './searchMatch';

/**
 * Пауза после последнего нажатия, прежде чем идти в справочник: иначе на
 * каждый символ уходил бы отдельный запрос.
 */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Минимум символов для поиска клиентов: по одному символу справочник
 * вернул бы почти всех клиентов организации.
 */
const MIN_CLIENT_QUERY_LENGTH = 2;

/** Сколько клиентов просить у бэкенда за раз. */
const CLIENT_RESULTS_LIMIT = 20;

export interface SearchDialogProps {
    open: boolean;
    onClose: () => void;
    /** Уже загруженные беседы: поиск по ним локальный, без запроса. */
    conversations: Conversation[];
    /** Организация консоли; `''` — не выбрана, клиентов искать негде. */
    mode: string;
    /** Переход в найденный чат. */
    onOpenChat: (conversationId: string) => void;
}

const CHAT_TAB = 0;
const CLIENT_TAB = 1;

/** Первая буква имени для аватара: у пустого имени — вопросительный знак. */
const avatarLetter = (name: string): string => (name.trim().charAt(0) || '?').toUpperCase();

/** Подпись чата: группа это или канал и, если известен, номер собеседника. */
const chatSubtitle = (conversation: Conversation): string => {
    const parts = [conversation.isGroup ? 'Группа' : channelLabel(conversation.type) || 'ORION'];
    if (conversation.contact?.phone) parts.push(conversation.contact.phone);

    return parts.join(' · ');
};

/** Подпись клиента: телефон и компания, если они заполнены. */
const clientSubtitle = (client: IClientListItem): string => {
    const parts = [client.phone, client.company].filter(
        (part): part is string => typeof part === 'string' && part.trim() !== '',
    );

    return parts.length > 0 ? parts.join(' · ') : 'Без телефона';
};

/**
 * Глобальный поиск, этап 1: чаты и клиенты.
 *
 * Чаты фильтруются на клиенте по уже загруженному списку бесед — поиск по
 * сообщениям будет отдельным этапом. Клиентов ищет бэкенд: справочник
 * организации не загружен в консоль целиком.
 *
 * Результат по чату открывает беседу, результат по клиенту — карточку,
 * минуя чат (так и записано в docs/ux-audit-frontend.md).
 */
export const SearchDialog: React.FC<SearchDialogProps> = ({
    open,
    onClose,
    conversations,
    mode,
    onOpenChat,
}) => {
    const [tab, setTab] = useState(CHAT_TAB);
    const [query, setQuery] = useState('');
    // Запрос, отправленный в справочник: обновляется с задержкой.
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [clients, setClients] = useState<IClientListItem[]>([]);
    const [clientsTotal, setClientsTotal] = useState(0);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientsError, setClientsError] = useState<string | null>(null);
    // Клиент, чью карточку показываем поверх поиска.
    const [cardClient, setCardClient] = useState<IClientListItem | null>(null);

    const hasMode = Boolean(mode && mode.trim());
    const trimmedQuery = query.trim();

    // Закрыли окно — следующий поиск начинается с чистого листа.
    useEffect(() => {
        if (open) return;

        setTab(CHAT_TAB);
        setQuery('');
        setDebouncedQuery('');
        setClients([]);
        setClientsTotal(0);
        setClientsError(null);
        setClientsLoading(false);
        setCardClient(null);
    }, [open]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [query]);

    // Справочник: запрос уходит только по вкладке клиентов и только когда
    // организация выбрана. Флаг `cancelled` отбрасывает ответ, пришедший
    // после смены запроса, — иначе на экране оказались бы чужие результаты.
    useEffect(() => {
        if (!open || tab !== CLIENT_TAB || !hasMode) {
            setClients([]);
            setClientsTotal(0);
            setClientsError(null);
            setClientsLoading(false);
            return;
        }

        const term = debouncedQuery.trim();
        if (term.length < MIN_CLIENT_QUERY_LENGTH) {
            setClients([]);
            setClientsTotal(0);
            setClientsError(null);
            setClientsLoading(false);
            return;
        }

        let cancelled = false;
        setClientsLoading(true);
        setClientsError(null);

        searchClients({ mode, query: term, perPage: CLIENT_RESULTS_LIMIT })
            .then((page) => {
                if (cancelled) return;
                setClients(page.items);
                setClientsTotal(page.total);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setClients([]);
                setClientsTotal(0);
                setClientsError(apiErrorMessage(error, 'Не удалось найти клиентов'));
            })
            .finally(() => {
                if (!cancelled) setClientsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, tab, hasMode, mode, debouncedQuery]);

    const chatResults = useMemo(
        () => matchConversations(conversations, query),
        [conversations, query],
    );

    const openChat = (conversationId: string): void => {
        onClose();
        onOpenChat(conversationId);
    };

    // Enter открывает первый результат активной вкладки: так поиск можно
    // использовать без мышки.
    const handleEnter = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'Enter') return;

        if (tab === CHAT_TAB && chatResults.length > 0) {
            event.preventDefault();
            openChat(chatResults[0].id);
            return;
        }

        if (tab === CLIENT_TAB && clients.length > 0) {
            event.preventDefault();
            setCardClient(clients[0]);
        }
    };

    const placeholder =
        tab === CHAT_TAB ? 'Название чата или номер телефона' : 'Имя, телефон или компания';

    const renderChats = (): React.ReactNode => {
        if (trimmedQuery === '') {
            return (
                <Box sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Введите название чата или номер телефона</Typography>
                </Box>
            );
        }

        if (chatResults.length === 0) {
            return (
                <Box sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Чаты не найдены</Typography>
                </Box>
            );
        }

        return (
            <List dense disablePadding>
                {chatResults.map((conversation) => {
                    const title = conversationSearchTitle(conversation);

                    return (
                        <ListItemButton key={conversation.id} onClick={() => openChat(conversation.id)}>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: conversation.isGroup ? 'primary.main' : 'grey.400' }}>
                                    {avatarLetter(title)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={title}
                                secondary={chatSubtitle(conversation)}
                                primaryTypographyProps={{ noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true }}
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        );
    };

    const renderClients = (): React.ReactNode => {
        if (!hasMode) {
            return (
                <Box sx={{ px: 2, py: 2 }}>
                    <Alert severity="info">
                        Выберите организацию в списке чатов, чтобы искать клиентов
                    </Alert>
                </Box>
            );
        }

        if (trimmedQuery === '') {
            return (
                <Box sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Введите имя, телефон или компанию клиента</Typography>
                </Box>
            );
        }

        if (trimmedQuery.length < MIN_CLIENT_QUERY_LENGTH) {
            return (
                <Box sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">
                        Введите минимум {MIN_CLIENT_QUERY_LENGTH} символа
                    </Typography>
                </Box>
            );
        }

        if (clientsLoading) {
            return (
                <Box sx={{ px: 2, py: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            );
        }

        if (clientsError) {
            return (
                <Box sx={{ px: 2, py: 2 }}>
                    <Alert severity="error">{clientsError}</Alert>
                </Box>
            );
        }

        if (clients.length === 0) {
            return (
                <Box sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Клиенты не найдены</Typography>
                </Box>
            );
        }

        return (
            <>
                <List dense disablePadding>
                    {clients.map((client) => (
                        <ListItemButton key={client.id} onClick={() => setCardClient(client)}>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                    {avatarLetter(client.name)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={client.name}
                                secondary={clientSubtitle(client)}
                                primaryTypographyProps={{ noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true }}
                            />
                        </ListItemButton>
                    ))}
                </List>
                {clientsTotal > clients.length && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', px: 2, py: 1 }}
                    >
                        Показано {clients.length} из {clientsTotal}: уточните запрос
                    </Typography>
                )}
            </>
        );
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="search-dialog-title">
                <DialogTitle
                    id="search-dialog-title"
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}
                >
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        Поиск
                    </Typography>
                    <IconButton onClick={onClose} size="small" title="Закрыть">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <Tabs
                    value={tab}
                    onChange={(_event, value: number) => setTab(value)}
                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Чаты" />
                    <Tab label="Клиенты" />
                </Tabs>

                <Box sx={{ px: 2, pt: 2 }} onKeyDown={handleEnter}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={placeholder}
                        inputProps={{ 'aria-label': 'Строка поиска' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <DialogContent dividers sx={{ px: 0, py: 0, minHeight: 260, maxHeight: '60vh' }}>
                    {tab === CHAT_TAB ? renderChats() : renderClients()}
                </DialogContent>
            </Dialog>

            <ClientCardDialog
                open={cardClient !== null}
                onClose={() => setCardClient(null)}
                mode={mode}
                phone={cardClient?.phone ?? undefined}
                initialClientId={cardClient?.id}
                clientName={cardClient?.name}
            />
        </>
    );
};

export default SearchDialog;
